/** @format */
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    EventEmitter,
    inject,
    input,
    Input,
    OnInit,
    Output,
    signal,
    ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CourseUnit, CourseUnitResultItem, CurriculumPeriod, CustomCourseUnitAttainment, OtmId } from 'common-typescript/types';
import { tap } from 'rxjs/operators';
import {
    AppErrorHandler,
    PlanStateObject,
    PlanStructureContext,
    StudyRightEntityService,
    UniversityService,
    UniversitySettingsEntityService,
} from 'sis-components';
import { UnplannedAttainments } from '../../../../../../plan-ng/plan-data.service';

@Component({
    selector: 'app-free-edit-course-unit-expandable',
    templateUrl: './free-edit-course-unit-expandable.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class FreeEditCourseUnitExpandableComponent implements OnInit {
    protected readonly planStructureContext = inject(PlanStructureContext);
    searchCurriculumPeriods = input<CurriculumPeriod[]>([]);
    allCourseUnits = input<CourseUnit[]>([]);
    allCustomCourseUnitAttainments = input<CustomCourseUnitAttainment[]>([]);
    unplannedAttainments = input<UnplannedAttainments>();
    @Input() planStateObject: PlanStateObject;
    @Output() toggledCourseUnit = new EventEmitter<{
        courseUnit: CourseUnit;
        isInPlan: boolean;
    }>();
    @Output() toggledCustomCourseUnitAttainment = new EventEmitter<{
        customCourseUnitAttainment: CustomCourseUnitAttainment;
        isInPlan: boolean;
    }>();

    @Output() selectedCourseUnit = new EventEmitter<CourseUnitResultItem>();

    searchCurriculumPeriodIds = computed(() => this.searchCurriculumPeriods().map(cur => cur.id));

    readonly selectedCourseUnits = computed(() => {
        const savedOrUnplannedAttainedCourseUnitIds = new Set([
            ...this.savedCourseUnits().map(cu => cu.id),
            ...this.unplannedCourseUnitAttainments().map(a => a.courseUnitId),
        ]);
        return this.allCourseUnits().filter(cu => !savedOrUnplannedAttainedCourseUnitIds.has(cu.id));
    });

    readonly unplannedCourseUnitAttainments = computed(() => this.unplannedAttainments()?.courseUnitAttainments ?? []);
    readonly unplannedCustomCourseUnitAttainments = computed(() => this.unplannedAttainments()?.customCourseUnitAttainments ?? []);

    readonly savedCourseUnits = signal<CourseUnit[]>([]);
    readonly savedCustomCourseUnitAttainments = signal<CustomCourseUnitAttainment[]>([]);

    currentUniversityOrgId: OtmId;
    crossStudyFeaturesEnabled = false;
    cooperationNetworkIds: OtmId[] = null;
    curriculumPeriodIds: OtmId[] = null;

    constructor(
        private readonly studyRightEntityService: StudyRightEntityService,
        private readonly universityService: UniversityService,
        private readonly universitySettingsService: UniversitySettingsEntityService,
        private readonly appErrorHandler: AppErrorHandler,
        private readonly destroyRef: DestroyRef,
    ) {
        this.currentUniversityOrgId = this.universityService.getCurrentUniversityOrgId();
    }

    ngOnInit(): void {
        this.curriculumPeriodIds = this.searchCurriculumPeriodIds();

        if (this.planStateObject?.studyRight) {
            this.cooperationNetworkIds = this.studyRightEntityService.getValidCooperationNetworkIds(this.planStateObject.studyRight);
        }

        this.universitySettingsService
            .getById(this.currentUniversityOrgId)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                tap(settings => {
                    this.crossStudyFeaturesEnabled = !!settings?.frontendFeatureToggles?.crossStudyFeaturesEnabledForStudent;
                }),
                this.appErrorHandler.defaultErrorHandler(),
            )
            .subscribe();

        const plannedCourseUnitIds = new Set(Object.keys(this.planStructureContext.planData().courseUnitsById));
        this.savedCourseUnits.set(this.allCourseUnits().filter(cu => plannedCourseUnitIds.has(cu.id)));

        const plannedAttainmentIds = new Set(Object.keys(this.planStructureContext.planData().attainmentsById));
        this.savedCustomCourseUnitAttainments.set(this.allCustomCourseUnitAttainments().filter(ccua => plannedAttainmentIds.has(ccua.id)));
    }

    selectCourseUnitFromSelectList(courseUnit: CourseUnitResultItem): void {
        if (courseUnit) {
            this.selectedCourseUnit.emit(courseUnit);
        }
    }

    handleToggleCourseUnit(toggleData: { courseUnit: CourseUnit; isInPlan: boolean }): void {
        this.toggledCourseUnit.emit(toggleData);
    }

    handleToggleCustomCourseUnitAttainment(toggleData: {
        customCourseUnitAttainment: CustomCourseUnitAttainment;
        isInPlan: boolean;
    }): void {
        this.toggledCustomCourseUnitAttainment.emit(toggleData);
    }

    getCourseUnitIdsExcludedFromSearch() {
        return new Set(
            this.allCourseUnits()
                .map(item => item.id)
                .concat(Object.keys(this.planStructureContext.validatablePlan().courseUnitsById)),
        );
    }
}
